import { useEffect, useRef, useState, useCallback } from 'react';

const WS_RECONNECT_DELAY = 3000;
const WS_MAX_RECONNECT = 5;

/**
 * Custom hook for WebSocket real-time notifications.
 * Auto-connects, reconnects on failure, and provides notification state.
 */
export function useNotifications(orgId, userId) {
  const [notifications, setNotifications] = useState([]);
  const [connected, setConnected] = useState(false);
  const [connectedUsers, setConnectedUsers] = useState(0);
  const wsRef = useRef(null);
  const reconnectCount = useRef(0);
  const reconnectTimer = useRef(null);

  const addNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev].slice(0, 50)); // Keep last 50
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const connect = useCallback(() => {
    if (!orgId) return;

    // Build a same-origin-safe WS base. If REACT_APP_BACKEND_URL is an absolute
    // http(s) URL, mirror its scheme (http->ws, https->wss). If it's relative or
    // empty (same-origin deploys, e.g. the VPS behind an HTTPS proxy), derive the
    // scheme+host from the page location so an HTTPS page always yields wss://
    // (never the blocked ws://api/... mixed-content case).
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    let wsBase;
    if (/^https?:\/\//i.test(backendUrl)) {
      wsBase = backendUrl.replace(/^http/i, 'ws').replace(/\/+$/, '');
    } else {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      wsBase = `${proto}://${window.location.host}`;
    }
    const wsUrl = `${wsBase}/api/ws/notifications/${orgId}${userId ? `?user_id=${userId}` : ''}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        reconnectCount.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'connection_ack') {
            setConnectedUsers(data.connected_users || 0);
          } else if (data.type === 'pong') {
            // Heartbeat response, ignore
          } else {
            addNotification(data);
          }
        } catch (err) {
          console.error('WS message parse error:', err);
        }
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        // Auto-reconnect
        if (reconnectCount.current < WS_MAX_RECONNECT) {
          reconnectCount.current++;
          reconnectTimer.current = setTimeout(connect, WS_RECONNECT_DELAY);
        }
      };

      ws.onerror = () => {
        // Will trigger onclose
      };
    } catch (err) {
      console.error('WebSocket connection error:', err);
    }
  }, [orgId, userId, addNotification]);

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
    };
  }, [connect]);

  // Heartbeat ping every 30 seconds
  useEffect(() => {
    if (!connected) return;
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [connected]);

  return {
    notifications,
    connected,
    connectedUsers,
    addNotification,
    clearNotifications,
  };
}
